/**
 * Starting from one of the shop's templates.
 *
 * A template is drawn once, at one size, and offered on every product and
 * category the shop assigns it to. So taking one is not "open that document":
 * it is "put that artwork on this card". The card keeps what belongs to it -
 * which product it is, its size, its sides, its bleed and safe area and its
 * corners - and takes only the artwork.
 *
 *   sides     Matched by position: the template's front onto this front, its
 *             back onto this back. A product that names its sides keeps exactly
 *             those; one that does not gains any extra sides the template has.
 *   size      Where it differs, the artwork is scaled to fit and centred, as a
 *             whole - so a layout drawn for 90 x 50 arrives on 85 x 55 looking
 *             like itself rather than with every object where it was.
 *
 * The pictures in a template stay the template's. The server lets a design use
 * them (see Trimbox_Assets::for_design), which is what keeps them in the press
 * file without every customer's card copying the shop's photographs.
 */

import { api, getConfig, pagesFixed } from './api';
import { registerAssets } from './assets';
import type { Layer, Page, TrimboxDocument } from './model/types';
import { roundMm } from './model/units';

/** Fetches a template and makes it this card's artwork, as one undo step. */
export async function takeTemplate(uuid: string, commit: (doc: TrimboxDocument) => void, current: TrimboxDocument): Promise<void> {
  const config = getConfig();
  const { document, assets } = await api.template(config.product.id, uuid);

  registerAssets(assets);
  commit(adoptTemplate(document, current, pagesFixed()));
}

/**
 * The template's artwork on the current card.
 *
 * @param template What the shop drew.
 * @param current  The customer's card as it is now.
 * @param fixed    Whether the product names its sides, so none may be added.
 */
export function adoptTemplate(template: TrimboxDocument, current: TrimboxDocument, fixed: boolean): TrimboxDocument {
  const pages: Page[] = current.pages.map((page, index) => {
    const from = template.pages[index];

    if (!from) {
      // The template has fewer sides: this one starts empty rather than keeping
      // whatever was on it, because a template replaces the whole design.
      return { ...page, background: null, layers: [] };
    }

    return {
      ...page,
      background: from.background,
      layers: fitted(from.layers, from.size, page.size),
    };
  });

  // Extra sides only where the customer is free to have them.
  if (!fixed) {
    const last = current.pages[current.pages.length - 1];

    for (let index = current.pages.length; index < template.pages.length; index++) {
      const from = template.pages[index]!;
      const size = last ? last.size : from.size;

      pages.push({
        ...from,
        id: uniquePageId(pages, from.id),
        size,
        // The shape belongs to the product, not to the template: a round
        // product's extra side is round too, and taking the template's
        // squareness with it would print a circle's worth of artwork on a
        // square that gets cut to a circle it was never laid out for.
        ...(last?.cut ? { cut: last.cut } : {}),
        ...(last?.round ? { round: true } : {}),
        layers: fitted(from.layers, from.size, size),
      });
    }
  }

  return {
    ...current,
    pages,
    // Everything either document uses, so nothing the template brings is missing.
    fonts: union(current.fonts, template.fonts, (f) => `${f.family}|${f.weight}|${f.style}`),
    assets: union(current.assets, template.assets, (a) => a.id),
  };
}

/** Layers drawn on a page of one size, moved onto a page of another. */
function fitted(layers: Layer[], from: { w: number; h: number }, to: { w: number; h: number }): Layer[] {
  const same = Math.abs(from.w - to.w) < 0.01 && Math.abs(from.h - to.h) < 0.01;

  if (same || from.w <= 0 || from.h <= 0) return layers;

  const s = Math.min(to.w / from.w, to.h / from.h);
  const dx = (to.w - from.w * s) / 2;
  const dy = (to.h - from.h * s) / 2;

  return layers.map((layer) => scaled(layer, s, dx, dy));
}

/**
 * One layer, scaled about the page's corner and then moved.
 *
 * Group members hold page positions, so they take the same arithmetic as
 * everything else. Effects are already proportions of the object and scale by
 * themselves; paths are proportions of their box.
 */
function scaled(layer: Layer, s: number, dx: number, dy: number): Layer {
  const base = {
    x: roundMm(layer.x * s + dx),
    y: roundMm(layer.y * s + dy),
    w: roundMm(layer.w * s),
    h: roundMm(layer.h * s),
    shadow: layer.shadow ? { ...layer.shadow, x: roundMm(layer.shadow.x * s), y: roundMm(layer.shadow.y * s) } : layer.shadow,
  };

  switch (layer.type) {
    case 'text':
      return {
        ...layer,
        ...base,
        font: { ...layer.font, size: Math.round(layer.font.size * s * 100) / 100 },
        letterSpacing: roundMm(layer.letterSpacing * s),
        stroke: layer.stroke ? { ...layer.stroke, width: roundMm(layer.stroke.width * s) } : null,
      };

    case 'shape':
      return {
        ...layer,
        ...base,
        radius: roundMm(layer.radius * s),
        stroke: layer.stroke
          ? { ...layer.stroke, width: roundMm(layer.stroke.width * s), dash: layer.stroke.dash.map((d) => roundMm(d * s)) }
          : null,
      };

    case 'group':
      return { ...layer, ...base, children: layer.children.map((child) => scaled(child, s, dx, dy)) };

    default:
      return { ...layer, ...base } as Layer;
  }
}

function uniquePageId(pages: Page[], wanted: string): string {
  const taken = new Set(pages.map((page) => page.id));

  if (!taken.has(wanted)) return wanted;

  let n = pages.length + 1;

  while (taken.has(`p${n}`)) n++;

  return `p${n}`;
}

function union<T>(first: T[], second: T[], key: (item: T) => string): T[] {
  const seen = new Set(first.map(key));

  return first.concat(second.filter((item) => !seen.has(key(item)) && seen.add(key(item))));
}
